import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Send, Loader2, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  'How does Udvega predict burnout?',
  'What is the WIS score?',
  'How do I keep survey responses anonymous?',
  'When should I send reminders?',
];

export default function ChatbotPage() {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content:
        "Hello! I'm the Udvega AI Assistant. Ask me anything about employee wellness, burnout prediction, or how to run your reviews.",
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: msg }]);
    setLoading(true);
    try {
      const payload = { message: msg };
      if (sessionId) payload.session_id = sessionId;
      const { data } = await axios.post(`${API}/chat`, payload, { withCredentials: true });
      setSessionId(data.session_id);
      setMessages((prev) => [...prev, { role: 'assistant', content: data.response }]);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const hint =
        typeof detail === 'string'
          ? detail
          : "Sorry, I'm having trouble connecting right now. Please try again.";
      setMessages((prev) => [...prev, { role: 'assistant', content: hint }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 md:p-8 lg:p-10 max-w-3xl mx-auto h-full flex flex-col" data-testid="chatbot-page">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-semibold text-slate-800 flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-teal-600" /> AI Assistant
        </h1>
        <p className="text-sm text-slate-500 mt-1">Your workforce wellness co-pilot</p>
      </div>

      <div className="glass-card rounded-3xl flex-1 flex flex-col overflow-hidden min-h-[60vh]">
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  m.role === 'user'
                    ? 'bg-teal-600 text-white rounded-br-md'
                    : 'bg-white border border-slate-100 text-slate-700 rounded-bl-md shadow-sm'
                }`}
              >
                {m.content}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-white border border-slate-100 rounded-2xl rounded-bl-md px-4 py-2.5 shadow-sm">
                <Loader2 className="w-4 h-4 animate-spin text-teal-600" />
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        {messages.length <= 1 && (
          <div className="px-5 pb-3 flex flex-wrap gap-2">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                onClick={() => send(s)}
                className="text-xs text-slate-600 bg-white border border-slate-200 rounded-full px-3 py-1.5 hover:border-teal-400 hover:text-teal-600 transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        <div className="border-t border-slate-200 p-3 flex items-center gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && send()}
            placeholder="Ask about employee wellness..."
            className="flex-1 text-sm px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500"
            data-testid="chatbot-input"
          />
          <Button
            onClick={() => send()}
            disabled={loading || !input.trim()}
            className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl h-10 w-10 p-0"
            data-testid="chatbot-send"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
